import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import ButtonComponent from "../../../../common/components/button/button.component";
import billingsTaxiRouteListingUserPermissionsHelper from "./common/user-permissions/billings-taxi-route-listing-user-permission.helper";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import BillingsTaxiRouteListingViewMode from "./common/types/billings-taxi-route-listing-view-mode";
import BillingsTaxiRouteListingByRailyComponent from "./by-raily/billings-taxi-route-listing-by-raily.component";
import BillingsTaxiRouteListingByTaxiComponent from "./by-taxi/billings-taxi-route-listing-by-taxi.component";

type BillingsTaxiRouteListingProps = {};

const BillingsTaxiRouteListingComponent: FC<
  BillingsTaxiRouteListingProps
> = () => {
  const { user, selectedAppLanguage, setBreadcrumbs } = useAppContext();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.billingsTaxiRouteListing);

  const userPermissions = useMemo(
    () =>
      billingsTaxiRouteListingUserPermissionsHelper.getPermissions(
        user?.roles!
      ),
    []
  );

  const translations = useMemo(
    () => billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations(),
    [selectedAppLanguage]
  );

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getTaxiRouteListingBreadcrumbs();

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const [selectedViewMode, setSelectedViewMode] =
    useState<BillingsTaxiRouteListingViewMode | null>(null);

  const ChangeViewToRailyButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsTaxiRouteListingViewMode.RAILY)
        }
        title={translations.header.changeViewToRailyButtonTitle}
      >
        {translations.header.changeViewToRailyButtonText}
      </ButtonComponent>
    ),
    [translations]
  );

  const ChangeViewToCargoButton = useMemo(
    () => (
      <ButtonComponent
        type="brand"
        onClick={() =>
          setSelectedViewMode(BillingsTaxiRouteListingViewMode.TAXI)
        }
        title={translations.header.changeViewToTaxiButtonTitle}
      >
        {translations.header.changeViewToTaxiButtonText}
      </ButtonComponent>
    ),
    [translations]
  );

  const viewChangeButtonOptions = useMemo(
    () => [
      {
        viewMode: BillingsTaxiRouteListingViewMode.RAILY,
        buttonOptions: {
          button: ChangeViewToCargoButton,
          hasPermission: userPermissions.hasAccessToTaxiView,
        },
      },
      {
        viewMode: BillingsTaxiRouteListingViewMode.TAXI,
        buttonOptions: {
          button: ChangeViewToRailyButton,
          hasPermission: userPermissions.hasAccessToRailyView,
        },
      },
    ],
    [userPermissions, ChangeViewToCargoButton, ChangeViewToRailyButton]
  );

  const getViewChangeButtons = useCallback(
    (viewMode: BillingsTaxiRouteListingViewMode) => {
      const buttonOptionsForSelectedViewMode = viewChangeButtonOptions
        .filter((option) => option.viewMode === viewMode)
        .map((option) => option.buttonOptions);

      return buttonOptionsForSelectedViewMode
        .filter((option) => option.hasPermission)
        .map((option) => option.button);
    },
    [viewChangeButtonOptions]
  );

  const viewOptions = useMemo(
    () => [
      {
        mode: BillingsTaxiRouteListingViewMode.RAILY,
        component: (
          <BillingsTaxiRouteListingByRailyComponent
            actionButtons={getViewChangeButtons(
              BillingsTaxiRouteListingViewMode.RAILY
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToRailyView,
      },
      {
        mode: BillingsTaxiRouteListingViewMode.TAXI,
        component: (
          <BillingsTaxiRouteListingByTaxiComponent
            actionButtons={getViewChangeButtons(
              BillingsTaxiRouteListingViewMode.TAXI
            )}
          />
        ),
        hasPermission: userPermissions.hasAccessToTaxiView,
      },
    ],
    [getViewChangeButtons]
  );

  const getPossibleViewOptions = useCallback(() => {
    return viewOptions.filter((option) => option.hasPermission);
  }, []);

  const possibleViewOptions = useMemo(
    () => getPossibleViewOptions(),
    [getPossibleViewOptions]
  );

  useEffect(() => {
    if (possibleViewOptions.length === 0) {
      return;
    }
    setSelectedViewMode(possibleViewOptions[0].mode);
  }, [possibleViewOptions]);

  const SelectedViewComponent = useMemo(
    () =>
      viewOptions.find((option) => option.mode === selectedViewMode)?.component,
    [selectedViewMode, viewOptions]
  );

  if (!SelectedViewComponent) {
    return null;
  }

  return SelectedViewComponent;
};

export default BillingsTaxiRouteListingComponent;
